import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  IndianRupee, ShoppingCart, TrendingUp, Boxes, Truck, RotateCcw, Store, Plug, Wallet, Share2, Copy,
  Sparkles, Plus, Send, Package, Flame, ArrowRight, CheckCircle2, Circle,
} from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatCard, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import PlanGate from "./PlanGate";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

export default function AgentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: store } = useQuery({ queryKey: ["my-store"], queryFn: async () => (await api.get("/agent/store")).data });
  const { data: orders } = useQuery({ queryKey: ["agent-orders-mini"], queryFn: async () => (await api.get("/orders?limit=100")).data });
  const { data: ledger } = useQuery({ queryKey: ["agent-ledger-mini"], queryFn: async () => (await api.get("/accounting/ledger?limit=10")).data });
  const { data: channels } = useQuery({ queryKey: ["channel-profit"], queryFn: async () => (await api.get("/agent/channel-profit")).data });
  const { data: mp } = useQuery({ queryKey: ["agent-mp"], queryFn: async () => (await api.get("/agent/marketplace")).data });
  const { data: recs } = useQuery({ queryKey: ["ai-recs"], queryFn: async () => (await api.get("/agent/ai/recommendations")).data });
  const { data: aiCfg } = useQuery({ queryKey: ["ai-config"], queryFn: async () => (await api.get("/agent/ai/config")).data });
  const { data: planInfo, refetch: refetchPlan } = useQuery({ queryKey: ["my-plan"], queryFn: async () => (await api.get("/agent/plan")).data });
  const { data: ob } = useQuery({ queryKey: ["onboarding"], queryFn: async () => (await api.get("/agent/onboarding")).data });

  const items = orders?.items || [];
  const today = new Date().toISOString().slice(0, 10);
  const month = today.slice(0, 7);
  const todaySales = items.filter((o) => o.created_at?.slice(0, 10) === today && o.status !== "cancelled").reduce((s, o) => s + o.total, 0);
  const monthSales = items.filter((o) => o.created_at?.slice(0, 7) === month && o.status !== "cancelled").reduce((s, o) => s + o.total, 0);
  const profit = items.filter((o) => o.status !== "cancelled").reduce((s, o) => s + (o.estimated_profit || 0), 0);
  const pendingShip = items.filter((o) => ["pending", "confirmed", "processing", "packed"].includes(o.status)).length;
  const storeUrl = store?.store ? `${window.location.origin}/store/${store.store.slug}` : null;
  const mpStatus = (ch) => (mp?.accounts || []).find((a) => a.channel === ch);

  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);
  const ask = async () => {
    if (!question.trim()) return;
    setAsking(true);
    try {
      const { data } = await api.post("/agent/assistant", { question });
      setAnswer(data.answer);
    } catch {
      toast.error("Assistant unavailable right now");
    } finally {
      setAsking(false);
    }
  };

  if (planInfo && !planInfo.plan) {
    return (
      <div data-testid="plan-gate-wrap">
        <PlanGate onActivated={() => refetchPlan()} />
      </div>
    );
  }

  return (
    <div data-testid="agent-dashboard">
      {/* Greeting + Quick Actions */}
      <div className="mb-6" data-testid="agent-hero">
        <h1 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="agent-greeting">
          {greeting()}, {user?.name?.split(" ")[0]}
        </h1>
        <p className="mt-1 text-sm text-slate-400">Ready to sell today?</p>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="quick-actions">
          <Button data-testid="qa-add-product" onClick={() => navigate("/agent/products")} className="bg-orange-500 text-xs font-bold text-white hover:bg-orange-600">
            <Plus size={13} /> ADD PRODUCT
          </Button>
          <Button data-testid="qa-create-ai-ad" onClick={() => navigate("/agent/ai-studio")}
            className="bg-violet-600 text-xs font-bold text-white hover:bg-violet-500">
            <Sparkles size={13} /> CREATE AI AD
          </Button>
          <Button data-testid="qa-my-store" variant="outline" onClick={() => navigate("/agent/store")}
            className="border-slate-700 text-xs font-semibold text-slate-200 hover:bg-slate-800">
            <Store size={13} /> {store?.store ? "MY STORE" : "CREATE STORE"}
          </Button>
          <Button data-testid="qa-publish" variant="outline" onClick={() => navigate("/agent/products")}
            className="border-slate-700 text-xs font-semibold text-slate-200 hover:bg-slate-800">
            <Send size={13} /> PUBLISH PRODUCTS
          </Button>
          <Button data-testid="qa-orders" variant="outline" onClick={() => navigate("/agent/orders")}
            className="border-slate-700 text-xs font-semibold text-slate-200 hover:bg-slate-800">
            <ShoppingCart size={13} /> VIEW ORDERS
          </Button>
          <Button data-testid="qa-ship" variant="outline" onClick={() => navigate("/agent/orders")}
            className="border-slate-700 text-xs font-semibold text-slate-200 hover:bg-slate-800">
            <Truck size={13} /> TRACK ORDERS
          </Button>
        </div>
      </div>

      {ob && ob.percent < 100 && (
        <div data-testid="onboarding-card" className="mb-6 rounded-xl border border-blue-500/30 bg-[#111827] p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-100">Getting started — {ob.done}/{ob.total} complete</h3>
            <span data-testid="onboarding-percent" className="text-xs font-bold text-orange-400">{ob.percent}%</span>
          </div>
          <div className="mb-4 h-2 overflow-hidden rounded-full bg-slate-800">
            <div className="h-full rounded-full bg-gradient-to-r from-orange-500 to-emerald-500 transition-all" style={{ width: `${ob.percent}%` }} />
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {ob.steps.map((s) => (
              <Link key={s.key} to={s.path} data-testid={`onboarding-${s.key}`}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors ${s.done ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-400" : "border-slate-800 text-slate-300 hover:border-orange-500/40"}`}>
                {s.done ? <CheckCircle2 size={13} /> : <Circle size={13} className="text-slate-600" />} {s.label}
              </Link>
            ))}
          </div>
        </div>
      )}

      {!store?.store && (
        <div data-testid="store-cta" className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-orange-500/40 bg-gradient-to-r from-orange-500/15 to-transparent p-5">
          <div>
            <h3 className="text-lg font-bold text-slate-50" style={{ fontFamily: "Outfit, sans-serif" }}>Launch your online store in one click</h3>
            <p className="mt-1 text-xs text-slate-400">Pick a design, click setup — your store goes live with all your products.</p>
          </div>
          <Link to="/agent/store">
            <Button data-testid="setup-store-cta-btn" className="bg-orange-500 font-semibold text-white hover:bg-orange-600">
              <Store size={15} /> Set up my store
            </Button>
          </Link>
        </div>
      )}

      {/* AI Recommendations */}
      {recs?.length > 0 && (
        <div className="mb-6 rounded-xl border border-violet-500/30 bg-gradient-to-r from-violet-500/10 to-transparent p-5" data-testid="ai-recommendations">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-bold text-slate-100">
              <Flame size={15} className="text-orange-500" /> Products to promote today
            </h3>
            <span className="text-[10px] text-slate-500" data-testid="ai-credits">
              AI credits today: <b className="text-violet-300">{aiCfg?.credits?.remaining_today ?? "—"} / {aiCfg?.credits?.daily_limit ?? "—"}</b>
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
            {recs.map((p) => (
              <div key={p.id} data-testid={`rec-${p.sku}`} className="rounded-xl border border-slate-800 bg-[#111827] p-3">
                {p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className="mb-2 h-20 w-full rounded-lg object-cover" />}
                <p className="truncate text-[11px] font-semibold text-slate-200">{p.name}</p>
                <p className="text-[10px] text-slate-500">Profit potential: <span className="font-bold text-emerald-400">{fmtINR(p.margin)}</span></p>
                <p className="text-[9px] text-slate-600">{p.available} in stock</p>
                <button data-testid={`rec-ai-ad-${p.sku}`} onClick={() => navigate(`/agent/ai-studio?product=${p.id}`)}
                  className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg bg-violet-600 py-1.5 text-[10px] font-bold text-white transition-colors hover:bg-violet-500">
                  <Sparkles size={10} /> CREATE AI AD
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard testid="agent-today-sales" icon={IndianRupee} label="Today's Sales" value={fmtINR(todaySales)} />
        <StatCard testid="agent-month-sales" icon={TrendingUp} label="Monthly Sales" value={fmtINR(monthSales)} accent="text-emerald-400" />
        <StatCard testid="agent-orders" icon={ShoppingCart} label="Orders" value={orders?.total ?? 0} accent="text-cyan-400" />
        <StatCard testid="agent-profit" icon={Wallet} label="Profit (Est.)" value={fmtINR(profit)} accent="text-emerald-400" />
        <StatCard testid="agent-pending" icon={Truck} label="Pending Shipments" value={pendingShip} accent="text-amber-400" />
        <StatCard testid="agent-balance" icon={IndianRupee} label="Ledger Balance" value={fmtINR(ledger?.balance ?? 0)} accent="text-violet-400" />
        <StatCard testid="agent-store-products" icon={Boxes} label="Store Products" value={store?.store?.product_count ?? 0} accent="text-blue-400" />
        <StatCard testid="agent-returns" icon={RotateCcw} label="Returns" value={items.filter((o) => ["return_requested", "returned", "refunded"].includes(o.status)).length} accent="text-rose-400" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="channels-status">
          <h3 className="text-sm font-semibold text-slate-200">Selling Channels</h3>
          <p className="mb-4 mt-1 text-[10px] text-slate-500">Amazon & Flipkart connections are configured and managed by the IndiaFast admin team.</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg bg-slate-900/60 px-3 py-3">
              <div className="flex items-center gap-2.5">
                <Store size={16} className="text-orange-400" />
                <div>
                  <p className="text-xs font-medium text-slate-200">Own Store</p>
                  {storeUrl && <p className="font-mono text-[10px] text-slate-500">{store.store.slug}</p>}
                </div>
              </div>
              {store?.store ? (
                <div className="flex items-center gap-2">
                  <StatusBadge status="live" />
                  <button data-testid="copy-store-link" onClick={() => { navigator.clipboard.writeText(storeUrl); toast.success("Store link copied"); }}
                    className="rounded p-1.5 text-slate-500 hover:bg-slate-800 hover:text-slate-200"><Copy size={13} /></button>
                  <a data-testid="share-store-link" href={`https://wa.me/?text=${encodeURIComponent(`Shop from my store: ${storeUrl}`)}`} target="_blank" rel="noreferrer"
                    className="rounded p-1.5 text-slate-500 hover:bg-slate-800 hover:text-emerald-400"><Share2 size={13} /></a>
                </div>
              ) : (
                <Link to="/agent/store" data-testid="setup-store-link" className="text-xs font-medium text-orange-400 hover:text-orange-300">Set up now →</Link>
              )}
            </div>
            {["amazon", "flipkart"].map((ch) => {
              const acc = mpStatus(ch);
              return (
                <div key={ch} data-testid={`channel-${ch}`} className="flex items-center justify-between rounded-lg bg-slate-900/60 px-3 py-3">
                  <div className="flex items-center gap-2.5">
                    <Plug size={16} className="text-amber-400" />
                    <div>
                      <p className="text-xs font-medium capitalize text-slate-200">{ch}</p>
                      {acc?.seller_id && <p className="font-mono text-[10px] text-slate-500">Seller {acc.seller_id}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {acc ? <StatusBadge status={acc.status} /> : <span className="text-[10px] text-slate-500">Being set up by admin</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="channel-profit-card">
          <h3 className="mb-4 text-sm font-semibold text-slate-200">Profit by Channel</h3>
          {!channels?.length ? <p className="py-8 text-center text-xs text-slate-500">No sales yet — publish products and share your store.</p> : (
            <div className="space-y-2">
              {channels.map((c) => (
                <div key={c.channel} className="rounded-lg bg-slate-900/60 px-3 py-2.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-medium capitalize text-slate-200">{c.channel.replace(/_/g, " ")}</span>
                    <span className="font-semibold text-emerald-400">{fmtINR(c.profit)} profit</span>
                  </div>
                  <p className="mt-1 text-[10px] text-slate-500">
                    Sales {fmtINR(c.sales)} · Fees {fmtINR(c.fees)} · Shipping {fmtINR(c.shipping)} · {c.orders} orders
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="recent-orders">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-200">Recent Orders</h3>
          <div className="flex items-center gap-3">
            <Link to="/agent/ai-studio" data-testid="go-ai-studio" className="flex items-center gap-1 text-xs font-semibold text-violet-400 hover:text-violet-300">
              <Sparkles size={12} /> AI Studio <ArrowRight size={11} />
            </Link>
            <Link to="/agent/orders" data-testid="view-all-orders" className="text-xs text-orange-400 hover:text-orange-300">View all</Link>
          </div>
        </div>
        {!items.length ? <p className="py-6 text-center text-xs text-slate-500">No orders yet. Share your store link to start selling.</p> : (
          <div className="space-y-2">
            {items.slice(0, 5).map((o) => (
              <div key={o.id} className="flex items-center justify-between rounded-lg bg-slate-900/60 px-3 py-2 text-xs">
                <div>
                  <p className="font-mono text-slate-200">{o.order_no}</p>
                  <p className="text-[10px] text-slate-500">{o.customer?.name} · {o.customer?.city}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-slate-100">{fmtINR(o.total)}</span>
                  <StatusBadge status={o.status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 rounded-xl border border-violet-500/30 bg-[#111827] p-5" data-testid="assistant-card">
        <h3 className="mb-1 flex items-center gap-2 text-sm font-bold text-slate-100">
          <Sparkles size={15} className="text-violet-400" /> Business Assistant
        </h3>
        <p className="mb-3 text-[11px] text-slate-500">Ask anything about your sales, products or profit — answers use your real business data.</p>
        {answer && <p data-testid="assistant-answer" className="mb-3 rounded-lg bg-slate-900/70 px-3 py-2.5 text-xs leading-relaxed text-slate-200">{answer}</p>}
        <div className="flex gap-2">
          <input data-testid="assistant-input" value={question} onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder="e.g. Which products are most profitable?"
            className="flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-100 outline-none focus:border-violet-500" />
          <Button data-testid="assistant-ask-btn" onClick={ask} disabled={asking || !question.trim()}
            className="bg-violet-600 text-xs font-bold text-white hover:bg-violet-500">
            <Send size={12} /> {asking ? "…" : "Ask"}
          </Button>
        </div>
      </div>
    </div>
  );
}
